/* eslint-disable arrow-body-style */
/* eslint-disable import/order */
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  FormControl,
  FormLabel,
  Chip,
  Divider,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogContent,
  Box,
  Tab,
  Tabs,
  Badge,
  InputAdornment,
  TablePagination,
  ButtonGroup,
} from '@mui/material';
import { useDebounce } from 'use-debounce';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { Container } from 'reactstrap';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';

const Discount = () => {
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);
  const theme = useTheme();

  // State declarations
  const [discounts, setDiscounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  // Debounced search terms
  const [debouncedProductSearch] = useDebounce(productSearch, 300);
  const [debouncedCategorySearch] = useDebounce(categorySearch, 300);

  // Default form initial values
  const defaultInitialValues = useMemo(
    () => ({
      DiscountId: null,
      DiscountName: '',
      DiscountType: 'P',
      DiscountValue: 0,
      StartDate: new Date(),
      EndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      Active: 'Y',
      ApplyTo: 'products',
    }),
    []
  );

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  // Validation schema
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        DiscountName: Yup.string().required('Discount name is required'),
        DiscountValue: Yup.number()
          .min(0, 'Value must be positive')
          .when('DiscountType', {
            is: 'P',
            then: (schema) => schema.max(100, 'Percentage cannot exceed 100%'),
          })
          .required('Discount value is required'),
        StartDate: Yup.date().required('Start date is required'),
        EndDate: Yup.date()
          .min(Yup.ref('StartDate'), 'End date must be after start date')
          .required('End date is required'),
        Active: Yup.string().oneOf(['Y', 'N']).required('Status is required'),
      }),
    []
  );

  // Filtered data
  const filteredProducts = useMemo(() => {
    return products.filter(
      (product) =>
        product.Product_Details_Description?.toLowerCase().includes(debouncedProductSearch.toLowerCase()) ||
        product.Product_Details_Code?.toLowerCase().includes(debouncedProductSearch.toLowerCase())
    );
  }, [products, debouncedProductSearch]);

  const filteredCategories = useMemo(() => {
    return categories.filter((category) =>
      category.Category_Description?.toLowerCase().includes(debouncedCategorySearch.toLowerCase())
    );
  }, [categories, debouncedCategorySearch]);

  // Data fetching
  const getAllDiscounts = useCallback(async () => {
    try {
      showLoading();
      const response = await axios.get('Master/Discount/GetAllDiscounts');
      setDiscounts(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Failed to load discounts', 'error');
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading, openSnackBar]);

  const getAllProducts = useCallback(async () => {
    try {
      showLoading();
      const response = await axios.get('Master/Products/GetAllProducts');
      setProducts(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Failed to load products', 'error');
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading, openSnackBar]);

  const getAllCategories = useCallback(async () => {
    try {
      showLoading();
      const response = await axios.get('Master/SubCategory/GetAllSubCategory');
      setCategories(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Failed to load categories', 'error');
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading, openSnackBar]);

  // Initialize data
  useEffect(() => {
    const userId = sessionStorage.getItem('UserId');
    setUserId(userId);
    getAllDiscounts();
    getAllProducts();
    getAllCategories();
  }, [getAllDiscounts, getAllProducts, getAllCategories]);

  // Form submission
  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        showLoading();

        // First create/update the main discount record
        const discountResponse = edit
          ? await axios.post('Master/Discount/UpdateDiscount', {
              ...values,
              ModifiedBy: userId,
            })
          : await axios.post('Master/Discount/InsertDiscount', {
              ...values,
              CreatedBy: userId,
            });

        const discountId = edit ? values.DiscountId : discountResponse.data.DiscountId;

        // Process product mappings
        if (selectedProducts.length > 0) {
          if (edit) {
            // For updates, first delete all existing product mappings
            await axios.delete(`Master/Discount/DeleteAllProductMappingsForDiscount/${discountId}`);
          }

          // Insert new product mappings
          await Promise.all(
            selectedProducts.map((productId) =>
              axios.post('Master/Discount/InsertProductDiscount', {
                ProductId: productId,
                DiscountId: discountId,
              })
            )
          );
        }

        // Process category mappings
        if (selectedCategories.length > 0) {
          if (edit) {
            // For updates, first delete all existing category mappings
            await axios.delete(`Master/Discount/DeleteAllCategoryMappingsForDiscount/${discountId}`);
          }

          // Insert new category mappings
          await Promise.all(
            selectedCategories.map((categoryId) =>
              axios.post('Master/Discount/InsertCategoryDiscount', {
                CategoryId: categoryId,
                DiscountId: discountId,
              })
            )
          );
        }

        openSnackBar(`Discount ${edit ? 'updated' : 'created'} successfully!`, 'success');
        getAllDiscounts();
        handleCloseDialog();
        resetForm();
      } catch (e) {
        openSnackBar(e.response?.data?.message || 'Operation failed', 'error');
      } finally {
        hideLoading();
      }
    },
    [edit, selectedProducts, selectedCategories, userId, getAllDiscounts, showLoading, hideLoading, openSnackBar]
  );

  // Delete discount
  const deleteDiscount = useCallback(async () => {
    try {
      showLoading();
      await axios.delete(`Master/Discount/DeleteDiscount/${deleteId}`);
      getAllDiscounts();
      openSnackBar('Discount deleted successfully!', 'success');
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Delete failed', 'error');
    } finally {
      hideLoading();
      handleCloseDelete();
    }
  }, [deleteId, getAllDiscounts, showLoading, hideLoading, openSnackBar]);

  // Edit discount
  const handleEditClick = useCallback((discount) => {
    // Set initial form values
    setInitialValues({
      ...discount,
      StartDate: new Date(discount.StartDate),
      EndDate: new Date(discount.EndDate),
    });

    // Normalize IDs as numbers
    const productIds = discount.ProductIds ? discount.ProductIds.split(',').map((id) => Number(id.trim())) : [];
    const categoryIds = discount.CategoryIds ? discount.CategoryIds.split(',').map((id) => Number(id.trim())) : [];

    setSelectedProducts(productIds);
    setSelectedCategories(categoryIds);

    // Set active tab
    setActiveTab(productIds.length > 0 ? 0 : 1);

    setEdit(true);
    setOpen(true);
  }, []);

  // Dialog handlers
  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    setEdit(false);
    setInitialValues(defaultInitialValues);
    setSelectedProducts([]);
    setSelectedCategories([]);
    setActiveTab(0);
    setProductSearch('');
    setCategorySearch('');
  }, [defaultInitialValues]);

  const handleCloseDelete = useCallback(() => {
    setOpenDelete(false);
    setDeleteId(null);
  }, []);

  // Tab change
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const toggleProductSelection = useCallback((productId) => {
    setSelectedProducts((prev) =>
      prev.includes(Number(productId)) ? prev.filter((id) => id !== Number(productId)) : [...prev, Number(productId)]
    );
  }, []);

  const toggleCategorySelection = useCallback((categoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(Number(categoryId)) ? prev.filter((id) => id !== Number(categoryId)) : [...prev, Number(categoryId)]
    );
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }, []);

  // Memoized table row component
  const DiscountRow = React.memo(({ discount, onEdit, onDelete }) => (
    <TableRow hover>
      <TableCell>{discount.DiscountName}</TableCell>
      <TableCell>
        <Chip
          label={discount.DiscountType === 'P' ? 'Percentage' : 'Fixed'}
          size="small"
          color={discount.DiscountType === 'P' ? 'primary' : 'secondary'}
        />
      </TableCell>
      <TableCell>
        {discount.DiscountType === 'P' ? `${discount.DiscountValue}%` : `₹${discount.DiscountValue}`}
      </TableCell>
      <TableCell>
        {formatDate(discount.StartDate)} - {formatDate(discount.EndDate)}
      </TableCell>
      <TableCell>
        {discount.ProductIds
          ? `${discount.ProductIds.split(',').length} products`
          : discount.CategoryIds
          ? `${discount.CategoryIds.split(',').length} categories`
          : 'None'}
      </TableCell>
      <TableCell>
        <Chip
          label={discount.Active === 'Y' ? 'Active' : 'Inactive'}
          color={discount.Active === 'Y' ? 'success' : 'error'}
          size="small"
        />
      </TableCell>
      <TableCell align="center">
        <IconButton size="small" color="primary" onClick={() => onEdit(discount)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => onDelete(discount.DiscountId)}>
          <DeleteForeverIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  ));

  return (
    <Container maxWidth="xl" sx={{ pt: 2 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6" fontWeight="bold">
              Discount Management
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setOpen(true)}
              startIcon={<PlaylistAddIcon />}
            >
              Create Discount
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Discounts Table */}
      <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Discount Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Applied To</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {discounts.length > 0 ? (
              discounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((discount) => (
                <DiscountRow
                  key={discount.DiscountId}
                  discount={discount}
                  onEdit={handleEditClick}
                  onDelete={(id) => {
                    setDeleteId(id);
                    setOpenDelete(true);
                  }}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No discounts found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {discounts.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={discounts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      )}

      {/* Add/Edit Discount Dialog */}
      <Dialog maxWidth="lg" open={open} onClose={handleCloseDialog} fullWidth>
        <DialogTitle sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {edit ? 'Edit Discount' : 'Create Discount'}
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Discount Name"
                      name="DiscountName"
                      value={values.DiscountName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.DiscountName && !!errors.DiscountName}
                      helperText={touched.DiscountName && errors.DiscountName}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Start Date"
                      type="date"
                      name="StartDate"
                      value={values.StartDate ? new Date(values.StartDate).toISOString().split('T')[0] : ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.StartDate && !!errors.StartDate}
                      helperText={touched.StartDate && errors.StartDate}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="End Date"
                      type="date"
                      name="EndDate"
                      value={values.EndDate ? new Date(values.EndDate).toISOString().split('T')[0] : ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.EndDate && !!errors.EndDate}
                      helperText={touched.EndDate && errors.EndDate}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* <Grid item xs={12} sm={4}>
                    <FormControl component="fieldset" size="medium" fullWidth>
                      <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>
                        Discount Type
                      </FormLabel>
                      <ButtonGroup variant="outlined" aria-label="discount type" fullWidth size="medium">
                        <Button
                          variant={values.DiscountType === 'P' ? 'contained' : 'outlined'}
                          onClick={() => setFieldValue('DiscountType', 'P')}
                          size="medium"
                        >
                          Percentage
                        </Button>
                        <Button
                          variant={values.DiscountType === 'F' ? 'contained' : 'outlined'}
                          onClick={() => setFieldValue('DiscountType', 'F')}
                          size="medium"
                        >
                          Fixed
                        </Button>
                      </ButtonGroup>
                    </FormControl>
                  </Grid> */}

                  <Grid item xs={12} sm={4}>
                    <FormControl component="fieldset" size="small" fullWidth>
                      <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>
                        {`Value ${values.DiscountType === 'P' ? '(%)' : '(₹)'}`}
                      </FormLabel>
                      <TextField
                        fullWidth
                        size="small"
                        name="DiscountValue"
                        type="number"
                        value={values.DiscountValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.DiscountValue && !!errors.DiscountValue}
                        helperText={touched.DiscountValue && errors.DiscountValue}
                        InputProps={{
                          startAdornment:
                            values.DiscountType === 'P' ? null : (
                              <InputAdornment position="start">
                                <LocalOfferIcon fontSize="small" />
                              </InputAdornment>
                            ),
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl component="fieldset" size="small" fullWidth>
                      <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>
                        Active Status
                      </FormLabel>
                      <ButtonGroup variant="outlined" aria-label="active status" fullWidth size="medium">
                        <Button
                          variant={values.Active === 'Y' ? 'contained' : 'outlined'}
                          color={values.Active === 'Y' ? 'primary' : 'inherit'}
                          onClick={() => setFieldValue('Active', 'Y')}
                          size="medium"
                        >
                          Yes
                        </Button>
                        <Button
                          variant={values.Active === 'N' ? 'contained' : 'outlined'}
                          color={values.Active === 'N' ? 'error' : 'inherit'}
                          onClick={() => setFieldValue('Active', 'N')}
                          size="medium"
                        >
                          No
                        </Button>
                      </ButtonGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ mt: 1 }}>
                      <Tab
                        label={
                          <Badge badgeContent={selectedProducts.length} color="primary" max={99}>
                            Products
                          </Badge>
                        }
                      />
                      <Tab
                        label={
                          <Badge badgeContent={selectedCategories.length} color="primary" max={99}>
                            Categories
                          </Badge>
                        }
                      />
                    </Tabs>
                    <Divider />

                    {activeTab === 0 ? (
                      <Box sx={{ maxHeight: 400, overflow: 'auto', mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search products..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 1 }}
                        />
                        <TableContainer>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Product</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Price</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredProducts.length > 0 ? (
                                filteredProducts.map((product) => (
                                  <TableRow key={product.Product_Details_Id} hover>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        size="small"
                                        checked={selectedProducts.includes(Number(product.Product_Details_Id))}
                                        onChange={() => toggleProductSelection(product.Product_Details_Id)}
                                      />
                                    </TableCell>
                                    <TableCell>{product.Product_Details_Description}</TableCell>
                                    <TableCell>{product.Category_Description}</TableCell>
                                    <TableCell>₹{product.SellingPrice}</TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    No products found
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ) : (
                      <Box sx={{ maxHeight: 400, overflow: 'auto', mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search categories..."
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 1 }}
                        />
                        <TableContainer>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Category</TableCell>
                                <TableCell>Products</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredCategories.length > 0 ? (
                                filteredCategories.map((category) => (
                                  <TableRow key={category.Category_Id} hover>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        size="small"
                                        checked={selectedCategories.includes(Number(category.Category_Id))}
                                        onChange={() => toggleCategorySelection(category.Category_Id)}
                                      />
                                    </TableCell>
                                    <TableCell>{category.Category_Description}</TableCell>
                                    <TableCell>
                                      {products.filter((p) => p.Category_Id === category.Category_Id).length}
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    No categories found
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="center" gap={1} my={2}>
                      <Button variant="outlined" color="secondary" size="small" onClick={handleCloseDialog}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color={edit ? 'warning' : 'primary'} size="small">
                        {edit ? 'Update' : 'Create'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete this discount? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button size="small" onClick={handleCloseDelete}>
            Cancel
          </Button>
          <Button size="small" color="error" onClick={deleteDiscount}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Discount;
